import {
  ReportEmpty,
  ReportError,
  ReportLoading,
  ReportMetricCard,
  ReportPageHeader,
  formatMoneyTotals,
} from "@/components/reports/report-page";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { buildMetadata } from "@/lib/metadata";
import { ReportService } from "@/services/report-service";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { DollarSign, ShoppingCart, Users } from "lucide-react";
import { normalizeReportSearch, reportSearchSchema } from "./-report-search";

export const Route = createFileRoute("/_main/reports/sales-by-customer")({
  validateSearch: reportSearchSchema,
  component: SalesByCustomerRoute,
  head: () => ({
    meta: buildMetadata({
      title: "Sales by Customer",
      description: "Analyze orders and recorded source values by customer",
      keywords: ["reports", "sales", "customers"],
    }),
  }),
});

function SalesByCustomerRoute() {
  const range = normalizeReportSearch(Route.useSearch());
  const navigate = Route.useNavigate();
  const reportQuery = useQuery({
    queryKey: ["reports", "sales-by-customer", range.dateFrom, range.dateTo],
    queryFn: () => ReportService.getSalesByCustomer(range),
  });
  const report = reportQuery.data;

  return (
    <main className="space-y-7 p-4 md:p-8">
      <ReportPageHeader
        title="Sales by Customer"
        totalOrders={report?.order_count}
        range={range}
        onRangeChange={(nextRange) =>
          navigate({ search: nextRange, replace: true })
        }
      />

      {reportQuery.isLoading ? <ReportLoading /> : null}
      {reportQuery.isError ? <ReportError /> : null}

      {report ? (
        <>
          <section className="grid gap-5 md:grid-cols-3">
            <ReportMetricCard
              label="Customers"
              value={report.customer_count}
              icon={Users}
            />
            <ReportMetricCard
              label="Orders"
              value={report.order_count}
              icon={ShoppingCart}
            />
            <ReportMetricCard
              label="Recorded Value"
              value={formatMoneyTotals(report.totals)}
              detail="Currencies are reported separately"
              icon={DollarSign}
            />
          </section>

          <Card className="gap-0 overflow-hidden py-0 shadow-sm">
            <CardHeader className="py-5">
              <CardTitle className="font-medium text-base">
                Customer Breakdown ({report.customers.length} customers)
              </CardTitle>
              <p className="text-muted-foreground text-sm">
                Monetary totals use amounts recorded on source order lines and
                are never combined across currencies.
              </p>
            </CardHeader>
            {report.customers.length ? (
              <Table>
                <TableHeader className="bg-slate-50">
                  <TableRow>
                    <TableHead>Customer</TableHead>
                    <TableHead>Orders</TableHead>
                    <TableHead className="text-right">Recorded Value</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {report.customers.map((customer) => (
                    <TableRow key={customer.customer}>
                      <TableCell className="font-medium">
                        {customer.customer}
                      </TableCell>
                      <TableCell>{customer.order_count}</TableCell>
                      <TableCell className="text-right">
                        {formatMoneyTotals(customer.totals)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <CardContent className="pb-6">
                <ReportEmpty>No customer orders in this period.</ReportEmpty>
              </CardContent>
            )}
          </Card>
        </>
      ) : null}
    </main>
  );
}
